import React, {
  useState, useEffect, useCallback, useMemo, useRef
} from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../Components/Sidebar';
import { auth, db, storage } from '../firebaseConfig';
import {
  collection, getDocs, addDoc, query, orderBy
} from 'firebase/firestore';
import { doc, getDoc } from 'firebase/firestore';
import {
  ref, uploadBytes, getDownloadURL
} from 'firebase/storage';
import {
  FaTrashAlt, FaCloudUploadAlt, FaSpinner, FaPlus,
  FaCopy
} from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '../Style/FailureReport.css';

/* ───────── helpers ───────── */
const debounce = (fn, wait = 300) => {
  let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), wait); };
};
const todayStr = () => new Date().toISOString().slice(0, 10);
const pad = (num, len = 4) => String(num).padStart(len, '0');
const sanitizeFolder = s => (s || '').replace(/[^\w\- ]+/g, '_').slice(0, 40);
const isImg = name => /\.(png|jpe?g|gif|bmp|webp|svg)$/i.test(name);
const isPdf = name => /\.pdf$/i.test(name);
const isCsv = name => /\.csv$/i.test(name);

/* Build next ID more safely (max existing + 1) */
const computeNextId = async () => {
  const snap = await getDocs(collection(db, 'FailureReports'));
  if (snap.empty) return 'FR0001';
  let max = 0;
  snap.forEach(d => {
    const r = d.data().reportId;
    const num = parseInt(String(r || '').replace(/\D/g, '') || '0', 10);
    if (num > max) max = num;
  });
  return `FR${pad(max + 1)}`;
};

export default function FailureReport() {
  const navigate = useNavigate();

  /* Top-level state */
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [reportId, setReportId] = useState('FR—');
  const [jobs, setJobs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [info, setInfo] = useState({
    jobId: '', status: 'Pending', date: todayStr(),
    customer: '', equipment: '', serial: ''
  });

  /* Documents: each doc row has: name, desc, files[], previews[] */
  const [docs, setDocs] = useState([{
    name: '', desc: '', files: [], previews: []
  }]);

  const [errors, setErrors] = useState({
    jobId: false, customer: false, docs: []
  });

  const debouncedInfoChange = useMemo(() => debounce((k, v) => {
    setInfo(p => ({ ...p, [k]: v }));
    if (k === 'jobId' || k === 'customer') {
      setErrors(e => ({ ...e, [k]: !v }));
    }
  }, 280), []);

  /* ───────── fetch initial ───────── */
  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async user => {
      setLoading(true);
      if (!user) {
        toast.error('Please login to create a failure report.', { className: 'toast-error' });
        setLoading(false);
        return;
      }
      try {
        const [jobSnap, custSnap] = await Promise.all([
          getDocs(query(collection(db, 'Jobs'), orderBy('createdAt', 'desc'))),
          getDocs(collection(db, 'Customers'))
        ]);
        setJobs(jobSnap.docs
          .map(d => d.data().jobId)
          .filter(Boolean)
          .sort((a, b) =>
            parseInt(b.replace(/\D/g, ''), 10) - parseInt(a.replace(/\D/g, ''), 10)
          ));

        setCustomers(custSnap.docs.map(d => ({ id: d.id, ...d.data() })));

        setReportId(await computeNextId());
      } catch (err) {
        console.error(err);
        toast.error('Failed to load initial data.', { className: 'toast-error' });
      } finally {
        setLoading(false);
      }
    });
    return () => unsub();
  }, []);

  /* ───────── validation ───────── */
  const validate = useCallback(() => {
    const docErrs = docs.map(d => !d.name.trim() && d.files.length === 0);
    const errs = {
      jobId: !info.jobId,
      customer: !info.customer,
      docs: docErrs
    };
    setErrors(errs);
    return !errs.jobId && !errs.customer && !docErrs.some(Boolean);
  }, [info.jobId, info.customer, docs]);

  /* ───────── doc management ───────── */
  const updateRow = (idx, updater) => {
    setDocs(p => p.map((d, i) => (i === idx ? updater(d) : d)));
  };

  const changeDocField = (idx, key, value) => {
    updateRow(idx, d => ({ ...d, [key]: value }));
    if (key === 'name') {
      setErrors(e => ({
        ...e,
        docs: e.docs.map((er, i) => (i === idx ? (!value.trim() && docs[idx].files.length === 0) : er))
      }));
    }
  };

  const addDocRow = () => {
    setDocs(p => [...p, { name: '', desc: '', files: [], previews: [] }]);
    setErrors(e => ({ ...e, docs: [...e.docs, false] }));
  };

  const removeDocRow = idx => {
    if (docs.length === 1) {
      toast.error('You need at least one document row.', { className: 'toast-error' });
      return;
    }
    // revoke object URLs
    docs[idx].previews.forEach(pr => {
      if (pr.url.startsWith('blob:')) URL.revokeObjectURL(pr.url);
    });
    setDocs(p => p.filter((_, i) => i !== idx));
    setErrors(e => ({ ...e, docs: e.docs.filter((_, i) => i !== idx) }));
  };

  /* ───────── file handling per row ───────── */
  const addFiles = (idx, files) => {
    if (!files.length) return;
    const valid = [];
    for (const f of files) {
      if (f.size > 5 * 1024 * 1024) {
        toast.error(`${f.name} exceeds 5MB limit.`, { className: 'toast-error' });
        continue;
      }
      valid.push(f);
    }
    updateRow(idx, d => {
      if (d.files.length + valid.length > 10) {
        toast.error('Max 10 files per document row.', { className: 'toast-error' });
        return d;
      }
      const newPreviews = valid.map(f => ({
        url: URL.createObjectURL(f), name: f.name, type: detectType(f.name)
      }));
      return {
        ...d,
        files: [...d.files, ...valid],
        previews: [...d.previews, ...newPreviews]
      };
    });
    setErrors(e => ({
      ...e,
      docs: e.docs.map((er, i) =>
        i === idx ? false : er
      )
    }));
  };

  const detectType = name => {
    if (isImg(name)) return 'image';
    if (isPdf(name)) return 'pdf';
    if (isCsv(name)) return 'csv';
    return 'file';
  };

  const removeFile = (rowIdx, fileIdx) => {
    updateRow(rowIdx, d => {
      const prev = d.previews[fileIdx];
      if (prev?.url?.startsWith('blob:')) URL.revokeObjectURL(prev.url);
      return {
        ...d,
        files: d.files.filter((_, i) => i !== fileIdx),
        previews: d.previews.filter((_, i) => i !== fileIdx)
      };
    });
    // re-eval doc error if name empty & no files left
    setTimeout(() => {
      setErrors(e => ({
        ...e,
        docs: e.docs.map((er, i) =>
          i === rowIdx ? (!docs[rowIdx].name.trim() && docs[rowIdx].files.length === 0) : er
        )
      }));
    }, 0);
  };

  /* ───────── clear form ───────── */
  const clearForm = () => {
    setInfo({
      jobId: '', status: 'Pending', date: todayStr(),
      customer: '', equipment: '', serial: ''
    });
    // revoke all URLS
    docs.forEach(d => d.previews.forEach(p => {
      if (p.url.startsWith('blob:')) URL.revokeObjectURL(p.url);
    }));
    setDocs([{ name: '', desc: '', files: [], previews: [] }]);
    setErrors({ jobId: false, customer: false, docs: [false] });
  };

  const cancel = () => navigate('/Jobs');

  /* ───────── upload + save ───────── */
  const uploadAll = async () => {
    const results = [];
    for (let i = 0; i < docs.length; i++) {
      const row = docs[i];
      const cleanName = row.name.trim() || `Doc${i + 1}`;
      const folder = sanitizeFolder(cleanName) || `Doc${i + 1}`;
      const uploaded = await Promise.all(
        row.files.map(async f => {
          const storageRef = ref(storage, `FailureReports/${reportId}/${folder}/${f.name}`);
            await uploadBytes(storageRef, f);
            return {
              fileName: f.name,
              url: await getDownloadURL(storageRef),
              type: detectType(f.name)
            };
        })
      );
      results.push({
        name: cleanName,
        desc: row.desc.trim() || '—',
        files: uploaded
      });
    }
    return results;
  };

  const saveReport = async e => {
    e.preventDefault();
    if (!validate()) {
      toast.error('Please fill required fields.', { className: 'toast-error' });
      return;
    }
    const user = auth.currentUser;
    if (!user) {
      toast.error('Not authenticated.', { className: 'toast-error' });
      return;
    }
    try {
      setSaving(true);
      const uSnap = await getDoc(doc(db, 'users', user.uid));
      const { firstName = '', lastName = '' } = uSnap.exists() ? uSnap.data() : {};

      const payloadDocs = await uploadAll();

      await addDoc(collection(db, 'FailureReports'), {
        reportId,
        jobId: info.jobId,
        status: info.status,
        date: info.date || todayStr(),
        customer: info.customer,
        equipment: info.equipment.trim() || '—',
        serial: info.serial.trim() || '—',
        docs: payloadDocs,
        createdAt: new Date().toISOString(),
        authId: user.uid,
        createdBy: `${firstName} ${lastName}`.trim() || '—'
      });

      toast.success('Failure report saved!', { className: 'toast-success' });
      navigate('/Jobs');
    } catch (err) {
      console.error(err);
      toast.error('Failed to save report.', { className: 'toast-error' });
    } finally {
      setSaving(false);
    }
  };

  /* ───────── cleanup object URLs on unmount ───────── */
  useEffect(() => {
    return () => {
      docs.forEach(d => d.previews.forEach(p => {
        if (p.url.startsWith('blob:')) URL.revokeObjectURL(p.url);
      }));
    };
  }, [docs]);

  const copyId = () => {
    navigator.clipboard.writeText(reportId).then(() => {
      toast.success('Report ID copied!', { className: 'toast-success' });
    });
  };

  /* Skeleton while loading */
  if (loading) {
    return (
      <div className="failure-page">
        <Sidebar />
        <div className="failure-loading">
          <FaSpinner className="spin" size={54} />
          <p>Loading data…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="failure-page">
      <ToastContainer
        position="top-right"
        autoClose={2600}
        toastClassName={({ type }) => type === 'success' ? 'toast-success' : 'toast-error'}
      />
      <Sidebar />
      <main className="failure-main">
        <h2>New Failure Report</h2>
        <form className="failure-form" onSubmit={saveReport}>
          {/* ───────── Section: Meta ───────── */}
          <section className="panel">
            <div className="section-head">
              <h3>Failure Info</h3>
              <div className="inline-chip">
                <span>{reportId}</span>
                <button type="button" onClick={copyId} aria-label="Copy Report ID"><FaCopy /></button>
              </div>
            </div>
            <div className="grid meta-grid">
              <div className="field-group">
                <label>Job Number <span className="req">*</span></label>
                <select
                  value={info.jobId}
                  onChange={e => debouncedInfoChange('jobId', e.target.value)}
                  className={errors.jobId ? 'input-error' : ''}
                  aria-invalid={errors.jobId}
                >
                  <option value="">Select job…</option>
                  {jobs.map(j => <option key={j}>{j}</option>)}
                </select>
                {errors.jobId && <span className="error-text">Required</span>}
              </div>
              <div className="field-group">
                <label>Status</label>
                <select
                  value={info.status}
                  onChange={e => debouncedInfoChange('status', e.target.value)}
                >
                  {['Pending', 'In Progress', 'Completed', 'On Hold', 'Cancelled']
                    .map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div className="field-group">
                <label>Date</label>
                <input
                  type="date"
                  value={info.date}
                  onChange={e => debouncedInfoChange('date', e.target.value)}
                />
              </div>
              <div className="field-group">
                <label>Customer <span className="req">*</span></label>
                <select
                  value={info.customer}
                  onChange={e => debouncedInfoChange('customer', e.target.value)}
                  className={errors.customer ? 'input-error' : ''}
                  aria-invalid={errors.customer}
                >
                  <option value="">Select customer…</option>
                  {customers.map(c => (
                    <option key={c.id} value={c.name}>{c.name}</option>
                  ))}
                </select>
                {errors.customer && <span className="error-text">Required</span>}
              </div>
              <div className="field-group">
                <label>Equipment</label>
                <input
                  value={info.equipment}
                  placeholder="e.g. Hydraulic Pump"
                  onChange={e => debouncedInfoChange('equipment', e.target.value)}
                />
              </div>
              <div className="field-group">
                <label>Serial No.</label>
                <input
                  value={info.serial}
                  placeholder="Serial #"
                  onChange={e => debouncedInfoChange('serial', e.target.value)}
                />
              </div>
            </div>
          </section>

          {/* ───────── Section: Document Rows ───────── */}
          <section className="panel">
            <div className="section-head">
              <h3>Failure Documents</h3>
              <button
                type="button"
                className="btn-inline-add"
                onClick={addDocRow}
              >
                <FaPlus /> Add Doc
              </button>
            </div>

            <div className="docs-header">
              <div>Document Name <span className="req">*</span></div>
              <div>Description</div>
              <div>Files</div>
              <div></div>
            </div>

            {docs.map((d, i) => (
              <DocRow
                key={i}
                idx={i}
                data={d}
                error={errors.docs[i]}
                onChange={changeDocField}
                onAddFiles={files => addFiles(i, files)}
                onRemoveFile={fileIdx => removeFile(i, fileIdx)}
                onRemoveRow={() => removeDocRow(i)}
              />
            ))}

            {docs.length === 0 && (
              <div className="empty-hint">No document rows — add one.</div>
            )}
          </section>

          {/* Actions */}
          <section className="panel actions-panel">
            <div className="actions-bar">
              <button
                type="submit"
                className="btn-submit"
                disabled={saving}
              >
                {saving ? 'Saving…' : 'Save Failure Report'}
              </button>
              <button
                type="button"
                className="btn-clear"
                onClick={clearForm}
                disabled={saving}
              >
                Clear Form
              </button>
              <button
                type="button"
                className="btn-cancel"
                onClick={cancel}
                disabled={saving}
              >
                Cancel
              </button>
            </div>
          </section>
        </form>
      </main>

      {saving && (
        <div className="saving-overlay">
          <FaSpinner className="spin" size={64} />
          <p>Uploading & Saving…</p>
        </div>
      )}
    </div>
  );
}

/* ───────── Doc Row Component ───────── */
function DocRow({ idx, data, error, onChange, onAddFiles, onRemoveFile, onRemoveRow }) {
  const fileInputRef = useRef(null);
  const [drag, setDrag] = useState(false);

  const chooseFiles = () => fileInputRef.current?.click();
  const onInput = e => onAddFiles(Array.from(e.target.files || []));
  const dragOver = e => { e.preventDefault(); setDrag(true); };
  const dragLeave = () => setDrag(false);
  const drop = e => {
    e.preventDefault();
    setDrag(false);
    onAddFiles(Array.from(e.dataTransfer.files || []));
  };

  return (
    <div className="doc-row">
      <div className="field-wrap">
        <input
          placeholder="Name (or attach files)"
          value={data.name}
          onChange={e => onChange(idx, 'name', e.target.value)}
          className={error ? 'input-error' : ''}
          aria-invalid={error}
        />
        {error && <span className="error-text">Required (name or at least one file)</span>}
      </div>
      <div className="field-wrap">
        <input
          placeholder="Description"
          value={data.desc}
          onChange={e => onChange(idx, 'desc', e.target.value)}
        />
      </div>
      <div className="file-cell">
        <div
          className={`mini-upload ${drag ? 'drag' : ''}`}
          onDragOver={dragOver}
          onDragLeave={dragLeave}
          onDrop={drop}
          onClick={chooseFiles}
          role="button"
          tabIndex={0}
          onKeyDown={e => { if (e.key === 'Enter') chooseFiles(); }}
          aria-label={`Upload files for document row ${idx + 1}`}
        >
          <FaCloudUploadAlt size={18} />
          <span>{drag ? 'Drop now' : 'Click / Drag'}</span>
          <small>IMG / PDF / CSV</small>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,.pdf,.csv"
            multiple
            onChange={onInput}
            style={{ display: 'none' }}
          />
        </div>

        {data.previews.length > 0 && (
          <div className="chips-row">
            {data.previews.map((p, i) => (
              <FileChip
                key={i}
                file={p}
                onRemove={() => onRemoveFile(i)}
              />
            ))}
          </div>
        )}
      </div>

      <div className="row-actions">
        <button
          type="button"
          onClick={() => onRemoveRow()}
          title="Remove document row"
          className="btn-icon"
        >
          <FaTrashAlt />
        </button>
      </div>
    </div>
  );
}

/* ───────── File Chip ───────── */
function FileChip({ file, onRemove }) {
  const type = useMemo(() => {
    if (isImg(file.name)) return 'image';
    if (isPdf(file.name)) return 'pdf';
    if (isCsv(file.name)) return 'csv';
    return 'file';
  }, [file.name]);

  return (
    <div className={`file-chip ${type}`}>
      {type === 'image'
        ? <img src={file.url} alt={file.name} />
        : <span className="badge">{type.toUpperCase()}</span>
      }
      <span className="fname" title={file.name}>{file.name}</span>
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${file.name}`}
      >
        ×
      </button>
    </div>
  );
}
